import type { FC } from 'react';

import { useTranslation } from '@crehana/i18n';
import Inbox from '@crehana/icons/pictograms/Inbox';
// biome-ignore lint/style/noRestrictedImports: legacy import, will be migrated
import { colors } from '@crehana/ui';
// biome-ignore lint/style/noRestrictedImports: legacy import, will be migrated
import { useForm } from 'react-hook-form';

import { useAttributes } from '@/views/AdminDashboard/components/AttributesDrawer/hooks';
import TextFieldActions from '@/views/AdminDashboard/components/TextFieldActions';

import AttributeList from './components/AttributeList';
import { AttrWrapper, BoxCount } from './Steps.styles';

const { grayMain, blueLighter } = colors;
const MAX_LENGTH_ATTRIBUTE = 50;

interface Step1Props {
  isDark?: boolean;
}

interface AttributeFrom {
  attribute: string;
}

const Step1: FC<React.PropsWithChildren<Step1Props>> = ({ isDark }) => {
  const { t } = useTranslation();
  const { attributes, onCreateAttribute, loadingPOST } = useAttributes();

  const {
    handleSubmit,
    register,
    reset,
    watch,
    errors,
    setError,
    clearErrors,
    formState: { isValid, isDirty },
  } = useForm<AttributeFrom>({
    mode: 'onChange',
    defaultValues: { attribute: '' },
  });

  const onSubmit = async ({ attribute }: AttributeFrom) => {
    const isDuplicated = attributes?.filter(
      attr => attr.name === attribute,
    ).length;

    if (isDuplicated) {
      setError('attribute', {
        type: 'duplicated',
        message: t('ATTR_ATTR_DUPLICATED'),
      });
    } else {
      await onCreateAttribute(attribute);
      reset();
    }
  };

  const counterWatch = watch('attribute')?.length || 0;

  return (
    <>
      <section className="flex flex-row mb-40">
        <form className="flex" onSubmit={handleSubmit(onSubmit)}>
          <TextFieldActions
            isDark={isDark}
            name="attribute"
            hint={t('ATTR_INPUT_ADD')}
            disabled={loadingPOST}
            maxLength={MAX_LENGTH_ATTRIBUTE}
            valueLength={counterWatch}
            submitDisabled={!isValid || !isDirty || loadingPOST}
            errorText={errors.attribute?.message}
            onChange={e => e.target.value === '' && clearErrors()}
            ref={register({
              required: {
                value: true,
                message: t('ATTR_INPUT_REQUIRED'),
              },
            })}
          />
        </form>
      </section>
      <section className="flex mb-12">
        <p className="font-subtitle3 mr-8">{t('ATTR_COUNT_LABEL')}</p>
        <BoxCount className="font-caption" isDark={isDark}>
          {attributes?.length || 0}
        </BoxCount>
      </section>
      <AttrWrapper isDark={isDark}>
        {attributes?.length === 0 ? (
          <div className="flex flex-col items-center justify-center mt-144 mb-144">
            <Inbox color={isDark ? blueLighter : grayMain} size={48} />
            <p className="font-body4">{t('ATTR_COUNT_EMPTY')}</p>
          </div>
        ) : (
          <div className="flex flex-col w-full h-full">
            {attributes.map((attribute, i) => (
              <AttributeList
                // biome-ignore lint/suspicious/noArrayIndexKey: index as key is acceptable here
                key={`attribute-${i}`}
                attribute={attribute}
                isDark={isDark}
              />
            ))}
          </div>
        )}
      </AttrWrapper>
    </>
  );
};

export default Step1;
